import { z } from 'zod';
export const ProfileCreatedSchema = z.object({
    userId: z.string().uuid(),
    nickname: z.string().min(2).max(10),
    createdAt: z.string().datetime()
}).strict();
export const NicknameUpdatedSchema = z.object({
    userId: z.string().uuid(),
    oldNickname: z.string(),
    newNickname: z.string().min(2).max(10),
    updatedAt: z.string().datetime()
}).strict();
export const ProfileImageUpdatedSchema = z.object({
    userId: z.string().uuid(),
    profileImageUrl: z.string().nullable().optional(),
    updatedAt: z.string().datetime()
}).strict();
export const BioUpdatedSchema = z.object({
    userId: z.string().uuid(),
    bio: z.string().max(500).nullable().optional(),
    updatedAt: z.string().datetime()
}).strict();
export const NotificationSettingsUpdatedSchema = z.object({
    userId: z.string().uuid(),
    pushNotifications: z.boolean(),
    emailNotifications: z.boolean(),
    messageNotifications: z.boolean(),
    broadcastStartNotifications: z.boolean(),
    orderUpdateNotifications: z.boolean(),
    dealerApplicationNotifications: z.boolean(),
    channels: z.array(z.enum(['PUSH', 'IN_APP'])),
    quietHoursEnabled: z.boolean(),
    quietHoursStart: z.string().regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/).nullable().optional(),
    quietHoursEnd: z.string().regex(/^([01][0-9]|2[0-3]):[0-5][0-9]$/).nullable().optional(),
    updatedAt: z.string().datetime()
}).strict();
export const AdminAccountCreatedSchema = z.object({
    adminId: z.string().uuid(),
    username: z.string().min(3).max(50),
    adminLevel: z.enum(['SUPER_ADMIN']),
    createdAt: z.string().datetime(),
    createdBy: z.string().uuid().nullable().optional()
}).strict();
export const AdminAccountUpdatedSchema = z.object({
    adminId: z.string().uuid(),
    username: z.string().min(3).max(50),
    adminLevel: z.enum(['SUPER_ADMIN']),
    adminStatus: z.enum(['ACTIVE', 'SUSPENDED', 'DELETED']),
    updatedAt: z.string().datetime(),
    updatedBy: z.string().uuid()
}).strict();
export const AdminAccountDeletedSchema = z.object({
    adminId: z.string().uuid(),
    username: z.string().min(3).max(50),
    deletedAt: z.string().datetime(),
    deletedBy: z.string().uuid()
}).strict();
export const AdminLoggedInSchema = z.object({
    adminId: z.string().uuid(),
    username: z.string().min(3).max(50),
    loginAt: z.string().datetime(),
    ipAddress: z.string().nullable().optional()
}).strict();
export const AdminPasswordChangedSchema = z.object({
    adminId: z.string().uuid(),
    username: z.string().min(3).max(50),
    changedAt: z.string().datetime(),
    changedBy: z.string().uuid()
}).strict();
export const UserAccountRegisteredSchema = z.object({
    userId: z.string().uuid(),
    socialProvider: z.enum(['GOOGLE', 'APPLE', 'KAKAO', 'NAVER']),
    socialProviderId: z.string(),
    registeredAt: z.string().datetime()
}).strict();
export const UserAccountAgoraUserIdAssignedSchema = z.object({
    userId: z.string().uuid(),
    agoraUserId: z.number().int(),
    assignedAt: z.string().datetime()
}).strict();
export const UserAccountLoggedInSchema = z.object({
    userId: z.string().uuid(),
    socialProvider: z.string(),
    loggedInAt: z.string().datetime(),
    ipAddress: z.string().nullable().optional()
}).strict();
export const UserAccountPhoneVerifiedSchema = z.object({
    userId: z.string().uuid(),
    phoneNumber: z.string(),
    verifiedAt: z.string().datetime()
}).strict();
export const UserAccountEmailUpdatedSchema = z.object({
    userId: z.string().uuid(),
    oldEmail: z.string().nullable().optional(),
    newEmail: z.string(),
    updatedAt: z.string().datetime()
}).strict();
export const UserAccountPhoneNumberUpdatedSchema = z.object({
    userId: z.string().uuid(),
    phoneNumber: z.string(),
    updatedAt: z.string().datetime()
}).strict();
export const UserAccountRoleAssignedSchema = z.object({
    userId: z.string().uuid(),
    role: z.string(),
    assignedBy: z.string().uuid(),
    assignedAt: z.string().datetime()
}).strict();
export const UserAccountSuspendedSchema = z.object({
    userId: z.string().uuid(),
    suspendedBy: z.string().uuid(),
    suspensionType: z.enum(['TEMPORARY', 'PERMANENT']),
    suspensionReason: z.string(),
    suspendedAt: z.string().datetime(),
    expiresAt: z.string().datetime().nullable().optional()
}).strict();
export const UserAccountSuspensionLiftedSchema = z.object({
    userId: z.string().uuid(),
    liftedBy: z.string().uuid(),
    liftedAt: z.string().datetime()
}).strict();
export const UserAccountWithdrawnSchema = z.object({
    userId: z.string().uuid(),
    withdrawnAt: z.string().datetime()
}).strict();
export const MessageSentSchema = z.object({
    messageId: z.string().uuid(),
    roomId: z.string(),
    senderId: z.string(),
    content: z.string().min(1),
    messageType: z.enum(['TEXT_MESSAGE', 'IMAGE_MESSAGE', 'SYSTEM']),
    imageUrls: z.array(z.string()).optional(),
    sentAt: z.string().datetime(),
    isDeleted: z.boolean()
}).strict();
export const MessageDeletedSchema = z.object({
    messageId: z.string().uuid(),
    roomId: z.string(),
    senderId: z.string(),
    content: z.string().min(1),
    messageType: z.enum(['TEXT_MESSAGE', 'IMAGE_MESSAGE', 'SYSTEM']),
    imageUrls: z.array(z.string()).optional(),
    sentAt: z.string().datetime(),
    isDeleted: z.boolean()
}).strict();
export const SendMessageSchema = z.object({
    roomId: z.string(),
    content: z.string().min(1)
}).strict();
export const SendImageSchema = z.object({
    roomId: z.string(),
    imageUrl: z.string(),
    caption: z.string().optional()
}).strict();
export const ImageSentSchema = z.object({
    messageId: z.string().uuid(),
    roomId: z.string(),
    senderId: z.string(),
    content: z.string().min(1),
    messageType: z.enum(['TEXT_MESSAGE', 'IMAGE_MESSAGE', 'SYSTEM']),
    imageUrls: z.array(z.string()).optional(),
    sentAt: z.string().datetime(),
    isDeleted: z.boolean()
}).strict();
export const DeleteMessageSchema = z.object({
    roomId: z.string(),
    messageId: z.string().uuid()
}).strict();
export const UserMessageReceivedSchema = z.object({
    roomId: z.string(),
    messageId: z.string().uuid(),
    sender: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    content: z.string().min(1),
    messageType: z.enum(['TEXT_MESSAGE', 'IMAGE_MESSAGE']),
    imageUrls: z.array(z.string()).optional(),
    sentAt: z.string().datetime(),
    isDeleted: z.boolean()
}).strict();
export const OpenChatUserJoinedSchema = z.object({
    openChatRoomId: z.string().uuid(),
    user: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    joinedAt: z.string().datetime()
}).strict();
export const OpenChatUserLeftSchema = z.object({
    openChatRoomId: z.string().uuid(),
    user: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    leftAt: z.string().datetime()
}).strict();
export const OpenChatRoomUserKickedSchema = z.object({
    openChatRoomId: z.string().uuid(),
    kickedUserId: z.string().uuid(),
    kickedBy: z.string().uuid(),
    kickedAt: z.string().datetime()
}).strict();
export const OpenChatMessagePostedSchema = z.object({
    messageId: z.string().uuid(),
    openChatRoomId: z.string().uuid(),
    sender: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    messageType: z.enum(['TEXT_MESSAGE', 'IMAGE_MESSAGE']),
    content: z.string().min(1).optional(),
    imageUrl: z.string().optional(),
    postedAt: z.string().datetime(),
    isDeleted: z.boolean().optional(),
    deletedAt: z.string().datetime().optional()
}).strict();
export const OpenChatImagePostedSchema = z.object({
    messageId: z.string().uuid(),
    openChatRoomId: z.string().uuid(),
    sender: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    imageUrl: z.string(),
    imageUrls: z.array(z.string()).optional(),
    postedAt: z.string().datetime(),
    isDeleted: z.boolean().optional(),
    deletedAt: z.string().datetime().optional()
}).strict();
export const OpenChatRoomMessageDeletedSchema = z.object({
    openChatRoomId: z.string().uuid(),
    messageId: z.string().uuid(),
    deletedBy: z.string().uuid(),
    deletedAt: z.string().datetime()
}).strict();
export const OpenChatRoomClosedSchema = z.object({
    openChatRoomId: z.string().uuid(),
    closedBy: z.string().uuid(),
    closedAt: z.string().datetime()
}).strict();
export const OpenChatNoticeUpdatedSchema = z.object({
    openChatRoomId: z.string().uuid(),
    noticeBanner: z.string(),
    updatedBy: z.string().uuid(),
    updatedAt: z.string().datetime()
}).strict();
export const JoinOpenChatRoomSchema = z.object({
    openChatRoomId: z.string()
}).strict();
export const LeaveOpenChatRoomSchema = z.object({
    openChatRoomId: z.string()
}).strict();
export const PostOpenChatMessageSchema = z.object({
    openChatRoomId: z.string(),
    content: z.string().min(1)
}).strict();
export const PostOpenChatImageSchema = z.object({
    openChatRoomId: z.string().uuid(),
    imageUrl: z.string()
}).strict();
export const AuctionBidUpdateSchema = z.object({
    auctionId: z.string().uuid(),
    bidId: z.string().uuid(),
    bidder: z.object({
        bidderId: z.string().uuid().nullable(),
        anonymousNickname: z.string(),
        isAnonymous: z.boolean()
    }).strict(),
    bidAmount: z.object({
        amount: z.number(),
        currency: z.enum(['KRW'])
    }).strict(),
    newCurrentPrice: z.object({
        amount: z.number(),
        currency: z.enum(['KRW'])
    }).strict(),
    bidTime: z.string().datetime(),
    isPrivate: z.boolean(),
    minimumBidPrice: z.object({
        amount: z.number(),
        currency: z.enum(['KRW'])
    }).strict()
}).strict();
export const AuctionEndedSchema = z.object({
    auctionId: z.string().uuid(),
    broadcastId: z.string().uuid(),
    auctionType: z.enum(['GENERAL', 'FIRST_COME', 'BLIND']),
    winner: z.string().optional(),
    finalPrice: z.object({
        amount: z.number(),
        currency: z.enum(['KRW'])
    }).strict(),
    totalBids: z.number().int().min(0),
    endedAt: z.string().datetime()
}).strict();
export const AuctionJoinSchema = z.object({
    auctionId: z.string()
}).strict();
export const AuctionJoinedSchema = z.object({
    auctionId: z.string().uuid(),
    auctionStatus: z.enum(['SCHEDULED', 'ACTIVE', 'ENDED', 'CANCELLED']),
    currentPrice: z.object({
        amount: z.number(),
        currency: z.enum(['KRW'])
    }).strict(),
    endTime: z.string().datetime(),
    minimumBidPrice: z.object({
        amount: z.number(),
        currency: z.enum(['KRW'])
    }).strict()
}).strict();
export const AuctionLeaveSchema = z.object({
    auctionId: z.string()
}).strict();
export const AuctionStartedSchema = z.object({
    auctionId: z.string().uuid(),
    broadcastId: z.string().uuid(),
    auctionType: z.enum(['GENERAL', 'FIRST_COME', 'BLIND']),
    productName: z.string(),
    productImageUrl: z.string().nullable().optional(),
    startingPrice: z.object({
        amount: z.number(),
        currency: z.enum(['KRW'])
    }).strict(),
    startTime: z.string().datetime(),
    endTime: z.string().datetime()
}).strict();
export const GiveawayEntryUpdateSchema = z.object({
    giveawayId: z.string().uuid(),
    totalEntries: z.number(),
    entryStatus: z.enum(['OPEN', 'CLOSED', 'DRAWN']),
    winnerNickname: z.string().nullable().optional()
}).strict();
export const GiveawayJoinSchema = z.object({
    giveawayId: z.string()
}).strict();
export const GiveawayLeaveSchema = z.object({
    giveawayId: z.string()
}).strict();
export const GiveawayDrawResultSchema = z.object({
    giveawayId: z.string().uuid(),
    broadcastId: z.string().uuid(),
    participants: z.array(z.object({
        participantIndex: z.number().int(),
        nickname: z.string()
    }).strict()),
    winner: z.object({
        participantIndex: z.number().int(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict()
}).strict();
export const GroupBuyJoinSchema = z.object({
    groupBuySalesId: z.string()
}).strict();
export const GroupBuyLeaveSchema = z.object({
    groupBuySalesId: z.string()
}).strict();
export const GroupBuySlotUpdateSchema = z.object({
    groupBuySalesId: z.string().uuid(),
    totalSlots: z.number(),
    reservedSlots: z.number(),
    confirmedSlots: z.number(),
    availableSlots: z.number(),
    lastReservedSlotNumber: z.number().nullable().optional()
}).strict();
export const InstantPurchaseItemPurchasedSchema = z.object({
    instantPurchaseId: z.string().uuid(),
    broadcastId: z.string().uuid(),
    buyer: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    quantity: z.number().int().min(1),
    purchasedAt: z.string().datetime()
}).strict();
export const ListingFinalizedSchema = z.object({
    broadcastId: z.string().uuid(),
    listingType: z.enum(['AUCTION', 'INSTANT_PURCHASE', 'GIVEAWAY']),
    classification: z.enum(['SOLD', 'UNSOLD']),
    listingId: z.string().uuid(),
    finalizedAt: z.string().datetime()
}).strict();
export const BroadcastStartedSchema = z.object({
    broadcastId: z.string().uuid(),
    dealerId: z.string().uuid(),
    startedAt: z.string().datetime(),
    agoraChannelName: z.string().optional(),
    isTestBroadcast: z.boolean().nullable().optional()
}).strict();
export const BroadcastEndedSchema = z.object({
    broadcastId: z.string().uuid(),
    dealerId: z.string().uuid(),
    endedAt: z.string().datetime(),
    endReasonMessage: z.string().nullable().optional(),
    totalViewers: z.number().int(),
    durationSeconds: z.number().int(),
    isTestBroadcast: z.boolean().nullable().optional(),
    endCause: z.enum(['HOST', 'ADMIN_FORCE_END', 'DEALER_ROLE_REVOKED', 'HOST_SIGNAL_LOST', 'null']).nullable().optional()
}).strict();
export const BroadcastStatusUpdateSchema = z.object({
    broadcastId: z.string().uuid(),
    broadcastStatus: z.enum(['SCHEDULED', 'LIVE', 'ENDED']),
    startedAt: z.string().datetime().nullable().optional(),
    endedAt: z.string().datetime().nullable().optional()
}).strict();
export const BroadcastScreenStateUpdateSchema = z.object({
    broadcastId: z.string().uuid(),
    version: z.number().int().min(0),
    serverNow: z.string().datetime(),
    instantListings: z.array(z.object({
        listingType: z.enum(['AUCTION', 'IMMEDIATE_PURCHASE']),
        productId: z.string(),
        productName: z.string(),
        productImageUrl: z.string(),
        auctionId: z.string().uuid().optional(),
        auctionType: z.enum(['GENERAL', 'FIRST_COME', 'BLIND']).nullable().optional(),
        instantPurchaseId: z.string().uuid().optional(),
        currentHighestBidPrice: z.string().optional(),
        currentMinimumBidPrice: z.object({
            amount: z.number(),
            currency: z.enum(['KRW'])
        }).strict().optional(),
        currentPrice: z.object({
            amount: z.number(),
            currency: z.enum(['KRW'])
        }).strict().optional(),
        currentHighestBidder: z.string().optional(),
        auctionStatus: z.enum(['IN_PROGRESS', 'SUCCESSFUL_BID', 'NO_BID', 'CANCELLED']).optional(),
        successfulBidder: z.string().optional(),
        winningBidAmount: z.string().optional(),
        endsAt: z.string().datetime().optional(),
        displayUntil: z.string().datetime().nullable().optional(),
        instantPurchaseStatus: z.enum(['ON_SALE', 'SOLD_OUT', 'ENDED']).optional(),
        salesPrice: z.object({
            amount: z.number(),
            currency: z.enum(['KRW'])
        }).strict().optional(),
        remainingQuantity: z.number().int().optional(),
        totalQuantity: z.number().int().optional()
    }).strict()).optional(),
    instantPurchaseLocked: z.boolean().optional(),
    instantListingLimit: z.number().int().min(1).optional(),
    foregroundListing: z.object({
        listingType: z.enum(['AUCTION', 'IMMEDIATE_PURCHASE']),
        productId: z.string(),
        productName: z.string(),
        productImageUrl: z.string(),
        auctionId: z.string().uuid().optional(),
        auctionType: z.enum(['GENERAL', 'FIRST_COME', 'BLIND']).nullable().optional(),
        instantPurchaseId: z.string().uuid().optional(),
        currentHighestBidPrice: z.string().optional(),
        currentMinimumBidPrice: z.object({
            amount: z.number(),
            currency: z.enum(['KRW'])
        }).strict().optional(),
        currentPrice: z.object({
            amount: z.number(),
            currency: z.enum(['KRW'])
        }).strict().optional(),
        currentHighestBidder: z.string().optional(),
        auctionStatus: z.enum(['IN_PROGRESS', 'SUCCESSFUL_BID', 'NO_BID', 'CANCELLED']).optional(),
        successfulBidder: z.string().optional(),
        winningBidAmount: z.string().optional(),
        endsAt: z.string().datetime().optional(),
        displayUntil: z.string().datetime().nullable().optional(),
        instantPurchaseStatus: z.enum(['ON_SALE', 'SOLD_OUT', 'ENDED']).optional(),
        salesPrice: z.object({
            amount: z.number(),
            currency: z.enum(['KRW'])
        }).strict().optional(),
        remainingQuantity: z.number().int().optional(),
        totalQuantity: z.number().int().optional()
    }).strict().nullable().optional(),
    giveawaySummary: z.object({
        giveawayId: z.string(),
        status: z.enum(['IN_PROGRESS', 'DRAWING', 'COMPLETED']),
        participantCount: z.number().int().min(0),
        endsAt: z.string().datetime().nullable().optional(),
        productName: z.string(),
        productImageUrl: z.string(),
        eligibilityLabel: z.string().optional()
    }).strict().nullable().optional(),
    groupPurchaseSummary: z.array(z.object({
        groupPurchaseId: z.string(),
        productName: z.string(),
        currentParticipants: z.number().int().min(0),
        requiredParticipants: z.number().int().min(1)
    }).strict()),
    topPurchasers: z.array(z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable().optional(),
        totalAmount: z.number().int().min(0)
    }).strict()).optional()
}).strict();
export const BroadcastSalesSummaryUpdateSchema = z.object({
    broadcastId: z.string().uuid(),
    totalAmount: z.number().int().min(0),
    paidAmount: z.number().int().min(0),
    pendingAmount: z.number().int().min(0),
    pendingOrderCount: z.number().int().min(0),
    computedAt: z.string().datetime()
}).strict();
export const BroadcastListChangedSchema = z.object({});
export const UiConfigUrgentSchema = z.object({
    files: z.array(z.object({
        name: z.string(),
        version: z.number().int(),
        applyPolicy: z.enum(['URGENT', 'IMMEDIATE'])
    }).strict())
}).strict();
export const BroadcastChatMessageSchema = z.object({
    messageId: z.string().uuid(),
    broadcastId: z.string().uuid(),
    seq: z.number().int().min(1),
    senderId: z.string().uuid(),
    senderType: z.enum(['DEALER', 'BUYER', 'SYSTEM']),
    senderNickname: z.string(),
    senderAvatar: z.string().nullable().optional(),
    messageType: z.enum(['TEXT_MESSAGE', 'IMAGE_MESSAGE', 'SYSTEM']),
    content: z.string().nullable().optional(),
    imageUrl: z.string().nullable().optional(),
    sentAt: z.string().datetime(),
    isDeleted: z.boolean(),
    emphasis: z.enum(['ACCENT']).nullable().optional(),
    anonymousSelf: z.object({
        auctionId: z.string().uuid(),
        anonymousNickname: z.string()
    }).strict().nullable().optional(),
    clientMessageId: z.string().uuid().nullable().optional()
}).strict();
export const SendBroadcastChatMessageSchema = z.object({
    broadcastId: z.string().uuid(),
    content: z.string().min(1).nullable().optional(),
    imageUrl: z.string().nullable().optional(),
    clientMessageId: z.string().nullable().optional()
}).strict();
export const BroadcastChatMessageDeletedSchema = z.object({
    messageId: z.string().uuid(),
    broadcastId: z.string().uuid(),
    deletedBy: z.string().uuid(),
    deletedAt: z.string().datetime()
}).strict();
export const ViewerCountUpdateSchema = z.object({
    broadcastId: z.string().uuid(),
    viewerCount: z.number().int().min(0),
    changedAt: z.string().datetime()
}).strict();
export const BroadcastViewerDuplicateAccessSchema = z.object({
    broadcastId: z.string().uuid()
}).strict();
export const HostHeartbeatPingSchema = z.object({
    broadcastId: z.string().uuid()
}).strict();
export const HostConnectedSchema = z.object({
    broadcastId: z.string().uuid(),
    hostId: z.string().uuid(),
    connectedAt: z.string().datetime()
}).strict();
export const HostReconnectingSchema = z.object({
    broadcastId: z.string().uuid(),
    hostId: z.string().uuid(),
    reconnectingAt: z.string().datetime()
}).strict();
export const HostReconnectedSchema = z.object({
    broadcastId: z.string().uuid(),
    hostId: z.string().uuid(),
    reconnectedAt: z.string().datetime()
}).strict();
export const HostDisconnectedSchema = z.object({
    broadcastId: z.string().uuid(),
    hostId: z.string().uuid(),
    disconnectedAt: z.string().datetime(),
    reason: z.enum(['TIMEOUT', 'GRACEFUL'])
}).strict();
export const PingSchema = z.object({});
export const PongSchema = z.object({});
export const ChannelSubscribeSchema = z.object({
    channel: z.string().min(1)
}).strict();
export const ChannelUnsubscribeSchema = z.object({
    channel: z.string().min(1)
}).strict();
export const ChannelSubscribedSchema = z.object({
    channel: z.string(),
    subscribedAt: z.string().datetime()
}).strict();
export const ChannelUnsubscribedSchema = z.object({
    channel: z.string(),
    unsubscribedAt: z.string().datetime()
}).strict();
export const ConnectionSuccessSchema = z.object({
    socketId: z.string().min(1),
    userId: z.string().min(1),
    timestamp: z.string().datetime()
}).strict();
export const SocketPongSchema = z.object({});
export const ChannelErrorSchema = z.object({
    error: z.string().min(1),
    channel: z.string().min(1),
    message: z.string().min(1)
}).strict();
export const SharedWhoamiRequestSchema = z.object({});
export const JournalEntryPostedSchema = z.object({
    journalEntryId: z.string().uuid(),
    transactionId: z.string(),
    transactionType: z.enum(['PAYMENT', 'REFUND', 'SETTLEMENT', 'COMMISSION', 'ADJUSTMENT', 'REVERSAL']),
    entryLines: z.array(z.object({
        accountId: z.string().uuid(),
        entryType: z.enum(['DEBIT', 'CREDIT']),
        amount: z.object({
            amount: z.number(),
            currency: z.string()
        }).strict(),
        description: z.string()
    }).strict()).min(2),
    totalDebit: z.object({
        amount: z.number(),
        currency: z.string()
    }).strict(),
    totalCredit: z.object({
        amount: z.number(),
        currency: z.string()
    }).strict(),
    postedAt: z.string().datetime(),
    metadata: z.object({}).optional()
}).strict();
export const JournalEntryReversedSchema = z.object({
    journalEntryId: z.string().uuid(),
    originalJournalEntryId: z.string().uuid(),
    transactionId: z.string(),
    transactionType: z.enum(['PAYMENT', 'REFUND', 'SETTLEMENT', 'COMMISSION', 'ADJUSTMENT', 'REVERSAL']),
    entryLines: z.array(z.object({
        accountId: z.string().uuid(),
        entryType: z.enum(['DEBIT', 'CREDIT']),
        amount: z.object({
            amount: z.number(),
            currency: z.string()
        }).strict(),
        description: z.string()
    }).strict()).min(2),
    totalDebit: z.object({
        amount: z.number(),
        currency: z.string()
    }).strict(),
    totalCredit: z.object({
        amount: z.number(),
        currency: z.string()
    }).strict(),
    reversedAt: z.string().datetime(),
    correlationId: z.string().uuid(),
    metadata: z.object({}).optional()
}).strict();
export const DiscrepancyDetectedSchema = z.object({
    reconciliationReportId: z.string().uuid(),
    discrepancyId: z.string().uuid(),
    discrepancyType: z.enum(['AMOUNT_MISMATCH', 'MISSING_LEDGER_ENTRY', 'MISSING_EXTERNAL_RECORD', 'STATUS_MISMATCH']),
    severity: z.enum(['LOW', 'MEDIUM', 'HIGH', 'CRITICAL']),
    affectedRecords: z.array(z.object({
        recordId: z.string(),
        recordType: z.string(),
        context: z.string()
    }).strict()).min(1),
    expectedValue: z.string().optional(),
    actualValue: z.string().optional(),
    description: z.string(),
    detectedAt: z.string().datetime()
}).strict();
export const ReconciliationCompletedSchema = z.object({
    reconciliationReportId: z.string().uuid(),
    reconciliationPeriod: z.object({
        start: z.string().datetime(),
        end: z.string().datetime()
    }).strict(),
    targetContext: z.enum(['PAYMENT', 'SETTLEMENT']),
    totalLedgerEntries: z.number().int().min(0),
    totalExternalRecords: z.number().int().min(0),
    discrepancyCount: z.number().int().min(0),
    status: z.enum(['COMPLETED', 'FAILED']),
    completedAt: z.string().datetime()
}).strict();
export const AccountCreatedSchema = z.object({
    accountId: z.string().uuid(),
    accountCode: z.string(),
    accountName: z.string(),
    accountType: z.enum(['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE']),
    initialBalance: z.object({
        amount: z.number(),
        currency: z.string()
    }).strict(),
    parentAccountId: z.string().uuid().optional(),
    description: z.string().optional(),
    createdAt: z.string().datetime(),
    correlationId: z.string().uuid()
}).strict();
export const AccountBalanceUpdatedSchema = z.object({
    accountId: z.string().uuid(),
    accountCode: z.string(),
    accountName: z.string(),
    accountType: z.enum(['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE']),
    previousBalance: z.object({
        amount: z.number(),
        currency: z.string()
    }).strict(),
    newBalance: z.object({
        amount: z.number(),
        currency: z.string()
    }).strict(),
    journalEntryId: z.string().uuid(),
    updatedAt: z.string().datetime(),
    correlationId: z.string().uuid()
}).strict();
export const PlatformBalanceUpdatedSchema = z.object({
    dealer: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    previousBalance: z.object({
        amount: z.number(),
        currency: z.string()
    }).strict(),
    newBalance: z.object({
        amount: z.number(),
        currency: z.string()
    }).strict(),
    changeAmount: z.object({
        amount: z.number(),
        currency: z.string()
    }).strict(),
    changeType: z.enum(['PAYMENT_RECEIVED', 'SETTLEMENT_PAID', 'COMMISSION_DEDUCTED', 'REFUND_PROCESSED', 'ADJUSTMENT']),
    journalEntryId: z.string().uuid(),
    updatedAt: z.string().datetime(),
    correlationId: z.string().uuid()
}).strict();
export const ReconciliationStartedSchema = z.object({
    reportId: z.string().uuid(),
    reconciliationPeriod: z.object({
        startDate: z.string().datetime(),
        endDate: z.string().datetime()
    }).strict(),
    targetContext: z.enum(['PAYMENT', 'SETTLEMENT']),
    initiatedBy: z.enum(['SCHEDULED', 'MANUAL']),
    startedAt: z.string().datetime(),
    correlationId: z.string().uuid()
}).strict();
export const CompensationRecordedSchema = z.object({
    compensationId: z.string().uuid(),
    reportId: z.string().uuid(),
    discrepancyId: z.string().uuid(),
    compensationJournalEntryId: z.string().uuid(),
    compensationAmount: z.object({
        amount: z.number(),
        currency: z.string()
    }).strict(),
    compensationType: z.enum(['CREDIT_ADJUSTMENT', 'DEBIT_ADJUSTMENT', 'REVERSAL', 'MANUAL_CORRECTION']),
    description: z.string(),
    recordedBy: z.string(),
    recordedAt: z.string().datetime(),
    correlationId: z.string().uuid()
}).strict();
export const ProductListingCreatedSchema = z.object({
    productListingId: z.string().uuid(),
    dealerId: z.string().uuid(),
    name: z.string(),
    productCategoryIds: z.array(z.string().uuid()),
    price: z.object({
        amount: z.number(),
        currency: z.enum(['KRW', 'USD'])
    }).strict(),
    salesChannel: z.enum(['BROADCAST', 'MARKETPLACE']),
    createdAt: z.string().datetime()
}).strict();
export const ProductListingPublishedSchema = z.object({
    productListingId: z.string().uuid(),
    dealerId: z.string().uuid(),
    salesChannel: z.enum(['BROADCAST', 'MARKETPLACE']),
    publishedAt: z.string().datetime()
}).strict();
export const ProductListingArchivedSchema = z.object({
    productListingId: z.string().uuid(),
    archivedAt: z.string().datetime()
}).strict();
export const ProductSoldOutSchema = z.object({
    productListingId: z.string().uuid(),
    dealerId: z.string().uuid(),
    soldOutAt: z.string().datetime()
}).strict();
export const ProductDetailsUpdatedSchema = z.object({
    productListingId: z.string().uuid(),
    dealerId: z.string().uuid(),
    name: z.string(),
    description: z.string().optional(),
    updatedAt: z.string().datetime()
}).strict();
export const ProductPricingUpdatedSchema = z.object({
    productListingId: z.string().uuid(),
    dealerId: z.string().uuid(),
    price: z.object({
        amount: z.number(),
        currency: z.enum(['KRW', 'USD'])
    }).strict(),
    updatedAt: z.string().datetime()
}).strict();
export const ProductImageAddedSchema = z.object({
    productListingId: z.string().uuid(),
    dealerId: z.string().uuid(),
    imageUrl: z.string(),
    addedAt: z.string().datetime()
}).strict();
export const ProductImageRemovedSchema = z.object({
    productListingId: z.string().uuid(),
    dealerId: z.string().uuid(),
    imageUrl: z.string(),
    removedAt: z.string().datetime()
}).strict();
export const SalesChannelToggledSchema = z.object({
    productListingId: z.string().uuid(),
    dealerId: z.string().uuid(),
    salesChannel: z.enum(['BROADCAST', 'MARKETPLACE']),
    toggledAt: z.string().datetime()
}).strict();
export const ProductCategoriesUpdatedSchema = z.object({
    productListingId: z.string().uuid(),
    productCategoryIds: z.array(z.string().uuid()),
    updatedAt: z.string().datetime()
}).strict();
export const GroupBuyListingCreatedSchema = z.object({
    groupBuyListingId: z.string().uuid(),
    productListingId: z.string().uuid(),
    product: z.object({
        productListingId: z.string(),
        name: z.string(),
        thumbnailUrl: z.string().nullable(),
        price: z.number()
    }).strict(),
    dealerId: z.string().uuid(),
    broadcast: z.object({
        broadcastId: z.string().uuid(),
        title: z.string(),
        thumbnailUrl: z.string().nullable(),
        status: z.enum(['SCHEDULED', 'LIVE', 'ENDED'])
    }).strict(),
    totalSlots: z.number().int().min(1),
    slotPrice: z.object({
        amount: z.number(),
        currency: z.enum(['KRW', 'USD'])
    }).strict(),
    breakSchedule: z.string().datetime(),
    createdAt: z.string().datetime()
}).strict();
export const GroupBuyListingActivatedSchema = z.object({
    groupBuyListingId: z.string().uuid(),
    dealerId: z.string().uuid(),
    totalSlots: z.number().int().min(1),
    activatedAt: z.string().datetime()
}).strict();
export const BreakStartedSchema = z.object({
    groupBuyListingId: z.string().uuid(),
    broadcastId: z.string().uuid(),
    totalSlots: z.number().int().min(1),
    soldSlots: z.number().int().min(0),
    startedAt: z.string().datetime()
}).strict();
export const BreakCompletedSchema = z.object({
    groupBuyListingId: z.string().uuid(),
    dealerId: z.string().uuid(),
    totalSlots: z.number().int().min(1),
    soldSlots: z.number().int().min(0),
    completedAt: z.string().datetime()
}).strict();
export const GroupBuyCancelledSchema = z.object({
    groupBuyListingId: z.string().uuid(),
    dealerId: z.string().uuid(),
    reason: z.string().optional(),
    cancelledAt: z.string().datetime()
}).strict();
export const SlotConfigurationUpdatedSchema = z.object({
    groupBuyListingId: z.string().uuid(),
    totalSlots: z.number().int().min(1),
    slotPrice: z.object({
        amount: z.number(),
        currency: z.enum(['KRW', 'USD'])
    }).strict(),
    updatedAt: z.string().datetime()
}).strict();
export const SlotReservedSchema = z.object({
    groupBuyListingId: z.string().uuid(),
    slotNumber: z.number().int().min(1),
    buyerId: z.string().uuid(),
    reservedAt: z.string().datetime()
}).strict();
export const SlotReleasedSchema = z.object({
    groupBuyListingId: z.string().uuid(),
    slotNumber: z.number().int().min(1),
    buyerId: z.string().uuid(),
    releasedAt: z.string().datetime()
}).strict();
export const SlotPurchaseConfirmedSchema = z.object({
    groupBuyListingId: z.string().uuid(),
    slotNumber: z.number().int().min(1),
    buyerId: z.string().uuid(),
    paymentId: z.string().uuid(),
    confirmedAt: z.string().datetime()
}).strict();
export const PaymentAuthorizedSchema = z.object({
    paymentId: z.string().uuid(),
    order: z.object({
        orderId: z.string().uuid(),
        orderType: z.enum(['AUCTION', 'INSTANT_PURCHASE', 'GROUP_BUY', 'GIVEAWAY']),
        totalAmount: z.number(),
        orderStatus: z.enum(['CREATED', 'PAYMENT_REQUESTED', 'AWAITING_REPAYMENT', 'PAID', 'SHIPPED', 'DELIVERED', 'CONFIRMED', 'CANCELLED', 'RETURNED', 'EXPIRED'])
    }).strict(),
    buyer: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    amount: z.object({
        amount: z.number().int(),
        currency: z.string()
    }).strict(),
    pgTransactionId: z.string(),
    approvalNumber: z.string(),
    authorizedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const PaymentCapturedSchema = z.object({
    paymentId: z.string().uuid(),
    order: z.object({
        orderId: z.string().uuid(),
        orderType: z.enum(['AUCTION', 'INSTANT_PURCHASE', 'GROUP_BUY', 'GIVEAWAY']),
        totalAmount: z.number(),
        orderStatus: z.enum(['CREATED', 'PAYMENT_REQUESTED', 'AWAITING_REPAYMENT', 'PAID', 'SHIPPED', 'DELIVERED', 'CONFIRMED', 'CANCELLED', 'RETURNED', 'EXPIRED'])
    }).strict(),
    buyer: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    dealerId: z.string().uuid(),
    amount: z.object({
        amount: z.number().int(),
        currency: z.string()
    }).strict(),
    shippingFee: z.object({
        amount: z.number().int(),
        currency: z.string()
    }).strict(),
    platformCommission: z.object({
        amount: z.number().int(),
        currency: z.string()
    }).strict(),
    paymentMethodType: z.enum(['CREDIT_CARD', 'DEBIT_CARD', 'TOSS_PAY_EASY_PAYMENT']),
    completedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const PaymentFailedSchema = z.object({
    paymentId: z.string().uuid(),
    order: z.object({
        orderId: z.string().uuid(),
        orderType: z.enum(['AUCTION', 'INSTANT_PURCHASE', 'GROUP_BUY', 'GIVEAWAY']),
        totalAmount: z.number(),
        orderStatus: z.enum(['CREATED', 'PAYMENT_REQUESTED', 'AWAITING_REPAYMENT', 'PAID', 'SHIPPED', 'DELIVERED', 'CONFIRMED', 'CANCELLED', 'RETURNED', 'EXPIRED'])
    }).strict(),
    buyer: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    failedReason: z.string(),
    errorCode: z.string().optional(),
    failedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const PaymentCancelledSchema = z.object({
    paymentId: z.string().uuid(),
    order: z.object({
        orderId: z.string().uuid(),
        orderType: z.enum(['AUCTION', 'INSTANT_PURCHASE', 'GROUP_BUY', 'GIVEAWAY']),
        totalAmount: z.number(),
        orderStatus: z.enum(['CREATED', 'PAYMENT_REQUESTED', 'AWAITING_REPAYMENT', 'PAID', 'SHIPPED', 'DELIVERED', 'CONFIRMED', 'CANCELLED', 'RETURNED', 'EXPIRED'])
    }).strict(),
    buyer: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    dealerId: z.string().uuid(),
    originalAmount: z.object({
        amount: z.number().int(),
        currency: z.string()
    }).strict(),
    cancellationReason: z.string(),
    cancelledAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const PaymentMethodRegisteredSchema = z.object({
    paymentMethodId: z.string().uuid(),
    userId: z.string().uuid(),
    methodType: z.enum(['CREDIT_CARD', 'DEBIT_CARD', 'TOSS_PAY_EASY_PAYMENT']),
    lastFourDigits: z.string().regex(/^[0-9]{4}$/),
    isDefault: z.boolean(),
    registeredAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const PaymentMethodSetAsDefaultSchema = z.object({
    paymentMethodId: z.string().uuid(),
    userId: z.string().uuid(),
    setAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const PaymentMethodDeactivatedSchema = z.object({
    paymentMethodId: z.string().uuid(),
    userId: z.string().uuid(),
    methodType: z.enum(['CREDIT_CARD', 'DEBIT_CARD', 'TOSS_PAY_EASY_PAYMENT']),
    deactivatedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const PaymentMethodDeletedSchema = z.object({
    paymentMethodId: z.string().uuid(),
    userId: z.string().uuid(),
    methodType: z.enum(['CREDIT_CARD', 'DEBIT_CARD', 'TOSS_PAY_EASY_PAYMENT']),
    deletedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const PaymentMethodNicknameUpdatedSchema = z.object({
    paymentMethodId: z.string().uuid(),
    userId: z.string().uuid(),
    nickname: z.string().max(50),
    updatedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const RefundRequestedSchema = z.object({
    refundId: z.string().uuid(),
    paymentId: z.string().uuid(),
    orderId: z.string().uuid(),
    refundAmount: z.object({
        amount: z.number().int(),
        currency: z.string()
    }).strict(),
    refundReason: z.enum(['CUSTOMER_REQUEST', 'ORDER_CANCELLED', 'RETURN_APPROVED', 'PAYMENT_ERROR']),
    requestedBy: z.string().uuid(),
    requestedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const RefundProcessedSchema = z.object({
    refundId: z.string().uuid(),
    paymentId: z.string().uuid(),
    order: z.object({
        orderId: z.string().uuid(),
        orderType: z.enum(['AUCTION', 'INSTANT_PURCHASE', 'GROUP_BUY', 'GIVEAWAY']),
        totalAmount: z.number(),
        orderStatus: z.enum(['CREATED', 'PAYMENT_REQUESTED', 'AWAITING_REPAYMENT', 'PAID', 'SHIPPED', 'DELIVERED', 'CONFIRMED', 'CANCELLED', 'RETURNED', 'EXPIRED'])
    }).strict(),
    buyer: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    refundAmount: z.object({
        amount: z.number().int(),
        currency: z.string()
    }).strict(),
    pgRefundTransactionId: z.string(),
    gateway: z.enum(['TOSS_PAYMENTS', 'STRIPE']),
    processedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const RefundCompletedSchema = z.object({
    refundId: z.string().uuid(),
    paymentId: z.string().uuid(),
    orderId: z.string().uuid(),
    refundType: z.enum(['CANCELLATION', 'RETURN', 'SHIPPING_FEE']),
    buyer: z.object({
        userId: z.string().uuid(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    dealerId: z.string().uuid().nullable().optional(),
    refundAmount: z.number().int().min(1),
    currency: z.string(),
    refundReason: z.string(),
    completedAt: z.string().datetime()
}).strict();
export const RefundFailedSchema = z.object({
    refundId: z.string().uuid(),
    paymentId: z.string().uuid(),
    order: z.object({
        orderId: z.string().uuid(),
        orderType: z.enum(['AUCTION', 'INSTANT_PURCHASE', 'GROUP_BUY', 'GIVEAWAY']),
        totalAmount: z.number(),
        orderStatus: z.enum(['CREATED', 'PAYMENT_REQUESTED', 'AWAITING_REPAYMENT', 'PAID', 'SHIPPED', 'DELIVERED', 'CONFIRMED', 'CANCELLED', 'RETURNED', 'EXPIRED'])
    }).strict(),
    buyer: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    refundAmount: z.object({
        amount: z.number().int(),
        currency: z.string()
    }).strict(),
    failureReason: z.string(),
    errorCode: z.string(),
    failedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const CancellationRequestedSchema = z.object({
    cancellationId: z.string().uuid(),
    orderId: z.string().uuid(),
    fulfillmentOrderId: z.string().uuid(),
    buyerId: z.string().uuid(),
    dealerId: z.string().uuid(),
    initiatedBy: z.enum(['BUYER', 'SYSTEM', 'ADMIN']).optional(),
    reasonType: z.enum(['BUYER_CHANGED_MIND', 'BUYER_FOUND_BETTER_PRICE', 'DEALER_OUT_OF_STOCK', 'DEALER_CANNOT_FULFILL']),
    reasonDescription: z.string().optional(),
    requestedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const CancellationRejectedSchema = z.object({
    cancellationId: z.string().uuid(),
    orderId: z.string().uuid(),
    buyerId: z.string().uuid(),
    dealerId: z.string().uuid(),
    rejectionReason: z.string(),
    rejectedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const OrderCreatedSchema = z.object({
    orderId: z.string().uuid(),
    buyerId: z.string().uuid(),
    dealerId: z.string().uuid().nullable().optional(),
    broadcastId: z.string().uuid().nullable().optional(),
    productListingId: z.string().uuid(),
    orderType: z.string(),
    paymentTerms: z.enum(['MERCHANT_INITIATED', 'CUSTOMER_INITIATED']).nullable().optional(),
    totalAmount: z.number().int().min(0),
    currency: z.string(),
    shippingFee: z.number().int().min(0),
    shippingAddressId: z.string().uuid().nullable().optional(),
    createdAt: z.string().datetime()
}).strict();
export const OrderPaidSchema = z.object({
    order: z.object({
        orderId: z.string().uuid(),
        orderType: z.enum(['AUCTION', 'INSTANT_PURCHASE', 'GROUP_BUY', 'GIVEAWAY']),
        totalAmount: z.number(),
        orderStatus: z.enum(['CREATED', 'PAYMENT_REQUESTED', 'AWAITING_REPAYMENT', 'PAID', 'SHIPPED', 'DELIVERED', 'CONFIRMED', 'CANCELLED', 'RETURNED', 'EXPIRED'])
    }).strict(),
    buyer: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    dealerId: z.string().uuid(),
    paymentId: z.string().uuid(),
    currency: z.string(),
    paidAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const OrderShippedSchema = z.object({
    order: z.object({
        orderId: z.string().uuid(),
        orderType: z.enum(['AUCTION', 'INSTANT_PURCHASE', 'GROUP_BUY', 'GIVEAWAY']),
        totalAmount: z.number(),
        orderStatus: z.enum(['CREATED', 'PAYMENT_REQUESTED', 'AWAITING_REPAYMENT', 'PAID', 'SHIPPED', 'DELIVERED', 'CONFIRMED', 'CANCELLED', 'RETURNED', 'EXPIRED'])
    }).strict(),
    buyer: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    dealerId: z.string().uuid(),
    shipmentId: z.string().uuid(),
    trackingNumber: z.string(),
    carrier: z.enum(['KOREA_POST', 'CJ_LOGISTICS', 'HANJIN_SHIPPING', 'LOTTE_LOGISTICS', 'LOGEN_DELIVERY', 'DAESIN', 'KYUNGDONG_EXPRESS', 'ILYANG_LOGISTICS', 'CHUNIL_EXPRESS', 'HAPDONG_EXPRESS', 'GS_POSTBOX', 'CU_POST', 'KUNYOUNG_EXPRESS', 'WOORI_EXPRESS', 'NH_LOGISTICS']),
    shippedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const OrderDeliveredSchema = z.object({
    order: z.object({
        orderId: z.string().uuid(),
        orderType: z.enum(['AUCTION', 'INSTANT_PURCHASE', 'GROUP_BUY', 'GIVEAWAY']),
        totalAmount: z.number(),
        orderStatus: z.enum(['CREATED', 'PAYMENT_REQUESTED', 'AWAITING_REPAYMENT', 'PAID', 'SHIPPED', 'DELIVERED', 'CONFIRMED', 'CANCELLED', 'RETURNED', 'EXPIRED'])
    }).strict(),
    buyer: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    dealerId: z.string().uuid(),
    shipmentId: z.string().uuid(),
    deliveredAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const OrderConfirmedSchema = z.object({
    order: z.object({
        orderId: z.string().uuid(),
        orderType: z.enum(['AUCTION', 'INSTANT_PURCHASE', 'GROUP_BUY', 'GIVEAWAY']),
        totalAmount: z.number(),
        orderStatus: z.enum(['CREATED', 'PAYMENT_REQUESTED', 'AWAITING_REPAYMENT', 'PAID', 'SHIPPED', 'DELIVERED', 'CONFIRMED', 'CANCELLED', 'RETURNED', 'EXPIRED'])
    }).strict(),
    buyer: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    dealerId: z.string().uuid(),
    confirmationType: z.enum(['MANUAL', 'AUTOMATIC']),
    confirmedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const OrderCancelledSchema = z.object({
    order: z.object({
        orderId: z.string().uuid(),
        orderType: z.enum(['AUCTION', 'INSTANT_PURCHASE', 'GROUP_BUY', 'GIVEAWAY']),
        totalAmount: z.number(),
        orderStatus: z.enum(['CREATED', 'PAYMENT_REQUESTED', 'AWAITING_REPAYMENT', 'PAID', 'SHIPPED', 'DELIVERED', 'CONFIRMED', 'CANCELLED', 'RETURNED', 'EXPIRED'])
    }).strict(),
    buyer: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    dealerId: z.string().uuid(),
    cancelledAt: z.string().datetime(),
    initiatedBy: z.enum(['BUYER', 'SYSTEM', 'ADMIN']).nullable().optional(),
    cancellationReason: z.string().optional(),
    wasPaid: z.boolean().optional(),
    occurredAt: z.string().datetime()
}).strict();
export const ShipmentCreatedSchema = z.object({
    shipmentId: z.string().uuid(),
    fulfillmentOrderId: z.string().uuid(),
    shippingOption: z.enum(['STANDARD', 'QUICK_DIRECT']),
    carrier: z.string().optional(),
    trackingNumber: z.string().nullable().optional(),
    estimatedDelivery: z.string().datetime().optional(),
    createdAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const ShipmentInTransitSchema = z.object({
    shipmentId: z.string().uuid(),
    fulfillmentOrderId: z.string().uuid(),
    shippingOption: z.enum(['STANDARD', 'QUICK_DIRECT']),
    carrier: z.string().optional(),
    trackingNumber: z.string().nullable().optional(),
    inTransitAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const ShipmentOutForDeliverySchema = z.object({
    shipmentId: z.string().uuid(),
    fulfillmentOrderId: z.string().uuid(),
    shippingOption: z.enum(['STANDARD', 'QUICK_DIRECT']),
    carrier: z.string().optional(),
    trackingNumber: z.string().nullable().optional(),
    outForDeliveryAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const ShipmentTrackingUpdatedSchema = z.object({
    shipmentId: z.string().uuid(),
    fulfillmentOrderId: z.string().uuid(),
    shippingOption: z.enum(['STANDARD', 'QUICK_DIRECT']),
    trackingNumber: z.string().nullable().optional(),
    trackingUpdate: z.object({
        trackingStatus: z.string(),
        trackingLocation: z.string(),
        timestamp: z.string().datetime(),
        description: z.string().optional()
    }).strict(),
    updatedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const ShipmentDeliveredSchema = z.object({
    shipmentId: z.string().uuid(),
    fulfillmentOrderId: z.string().uuid(),
    shippingOption: z.enum(['STANDARD', 'QUICK_DIRECT']),
    trackingNumber: z.string().nullable().optional(),
    actualDelivery: z.string().datetime(),
    deliveredAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const ShipmentFailedSchema = z.object({
    shipmentId: z.string().uuid(),
    fulfillmentOrderId: z.string().uuid(),
    shippingOption: z.enum(['STANDARD', 'QUICK_DIRECT']),
    carrier: z.string().optional(),
    trackingNumber: z.string().nullable().optional(),
    failureReason: z.string(),
    failedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const ShipmentCreatedV2Schema = z.object({
    shipmentId: z.string().uuid(),
    fulfillmentOrderId: z.string().uuid(),
    shippingOption: z.enum(['STANDARD', 'QUICK_DIRECT']),
    carrier: z.string().optional(),
    trackingNumber: z.string().nullable().optional(),
    estimatedDelivery: z.string().datetime().optional(),
    createdAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const ShipmentInTransitV2Schema = z.object({
    shipmentId: z.string().uuid(),
    fulfillmentOrderId: z.string().uuid(),
    shippingOption: z.enum(['STANDARD', 'QUICK_DIRECT']),
    carrier: z.string().optional(),
    trackingNumber: z.string().nullable().optional(),
    inTransitAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const ShipmentOutForDeliveryV2Schema = z.object({
    shipmentId: z.string().uuid(),
    fulfillmentOrderId: z.string().uuid(),
    shippingOption: z.enum(['STANDARD', 'QUICK_DIRECT']),
    carrier: z.string().optional(),
    trackingNumber: z.string().nullable().optional(),
    outForDeliveryAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const ShipmentTrackingUpdatedV2Schema = z.object({
    shipmentId: z.string().uuid(),
    fulfillmentOrderId: z.string().uuid(),
    shippingOption: z.enum(['STANDARD', 'QUICK_DIRECT']),
    trackingNumber: z.string().nullable().optional(),
    trackingUpdate: z.object({
        trackingStatus: z.string(),
        trackingLocation: z.string(),
        timestamp: z.string().datetime(),
        description: z.string().optional()
    }).strict(),
    updatedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const ShipmentDeliveredV2Schema = z.object({
    shipmentId: z.string().uuid(),
    fulfillmentOrderId: z.string().uuid(),
    shippingOption: z.enum(['STANDARD', 'QUICK_DIRECT']),
    trackingNumber: z.string().nullable().optional(),
    actualDelivery: z.string().datetime(),
    deliveredAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const ShipmentFailedV2Schema = z.object({
    shipmentId: z.string().uuid(),
    fulfillmentOrderId: z.string().uuid(),
    shippingOption: z.enum(['STANDARD', 'QUICK_DIRECT']),
    carrier: z.string().optional(),
    trackingNumber: z.string().nullable().optional(),
    failureReason: z.string(),
    failedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const ReturnRequestedSchema = z.object({
    returnId: z.string().uuid(),
    order: z.object({
        orderId: z.string().uuid(),
        orderType: z.enum(['AUCTION', 'INSTANT_PURCHASE', 'GROUP_BUY', 'GIVEAWAY']),
        totalAmount: z.number(),
        orderStatus: z.enum(['CREATED', 'PAYMENT_REQUESTED', 'AWAITING_REPAYMENT', 'PAID', 'SHIPPED', 'DELIVERED', 'CONFIRMED', 'CANCELLED', 'RETURNED', 'EXPIRED'])
    }).strict(),
    buyerId: z.string().uuid(),
    returnReason: z.enum(['DEFECTIVE', 'WRONG_ITEM', 'NOT_AS_DESCRIBED', 'CHANGED_MIND', 'OTHER']),
    returnDescription: z.string().optional(),
    returnItems: z.array(z.object({
        orderItemId: z.string().uuid(),
        quantity: z.number().int().min(1)
    }).strict()),
    requestedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const ReturnApprovedSchema = z.object({
    returnId: z.string().uuid(),
    order: z.object({
        orderId: z.string().uuid(),
        orderType: z.enum(['AUCTION', 'INSTANT_PURCHASE', 'GROUP_BUY', 'GIVEAWAY']),
        totalAmount: z.number(),
        orderStatus: z.enum(['CREATED', 'PAYMENT_REQUESTED', 'AWAITING_REPAYMENT', 'PAID', 'SHIPPED', 'DELIVERED', 'CONFIRMED', 'CANCELLED', 'RETURNED', 'EXPIRED'])
    }).strict(),
    dealerId: z.string().uuid(),
    approvedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const ReturnRejectedSchema = z.object({
    returnId: z.string().uuid(),
    order: z.object({
        orderId: z.string().uuid(),
        orderType: z.enum(['AUCTION', 'INSTANT_PURCHASE', 'GROUP_BUY', 'GIVEAWAY']),
        totalAmount: z.number(),
        orderStatus: z.enum(['CREATED', 'PAYMENT_REQUESTED', 'AWAITING_REPAYMENT', 'PAID', 'SHIPPED', 'DELIVERED', 'CONFIRMED', 'CANCELLED', 'RETURNED', 'EXPIRED'])
    }).strict(),
    dealerId: z.string().uuid(),
    rejectionReason: z.string(),
    rejectedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const ReturnCompletedSchema = z.object({
    returnId: z.string().uuid(),
    order: z.object({
        orderId: z.string().uuid(),
        orderType: z.enum(['AUCTION', 'INSTANT_PURCHASE', 'GROUP_BUY', 'GIVEAWAY']),
        totalAmount: z.number(),
        orderStatus: z.enum(['CREATED', 'PAYMENT_REQUESTED', 'AWAITING_REPAYMENT', 'PAID', 'SHIPPED', 'DELIVERED', 'CONFIRMED', 'CANCELLED', 'RETURNED', 'EXPIRED'])
    }).strict(),
    refundedAt: z.string().datetime(),
    completedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const FulfillmentOrderShippedSchema = z.object({
    fulfillmentOrderId: z.string().uuid(),
    orderIds: z.array(z.string().uuid()),
    buyerId: z.string().uuid(),
    dealerId: z.string().uuid(),
    shippedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const FulfillmentOrderDeliveredSchema = z.object({
    fulfillmentOrderId: z.string().uuid(),
    orderIds: z.array(z.string().uuid()),
    buyerId: z.string().uuid(),
    dealerId: z.string().uuid(),
    deliveredAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const FulfillmentOrderConfirmedSchema = z.object({
    fulfillmentOrderId: z.string().uuid(),
    orderIds: z.array(z.string().uuid()),
    buyerId: z.string().uuid(),
    dealerId: z.string().uuid(),
    confirmationType: z.enum(['MANUAL', 'AUTOMATIC']),
    confirmedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const FulfillmentOrderConfirmationRequestedSchema = z.object({
    fulfillmentOrderId: z.string().uuid(),
    buyerId: z.string().uuid(),
    dealerId: z.string().uuid(),
    targetOrderId: z.string().uuid(),
    confirmationRequestCount: z.number().int().min(1),
    requestedAt: z.string().datetime()
}).strict();
export const FulfillmentOrderShippingAddressChangedSchema = z.object({
    fulfillmentOrderId: z.string().uuid(),
    fulfillmentOrderIds: z.array(z.string().uuid()).min(1),
    buyerId: z.string().uuid(),
    dealerId: z.string().uuid(),
    changedBy: z.enum(['DEALER', 'ADMIN']),
    previousAddressId: z.string().uuid().nullable(),
    newAddressId: z.string().uuid(),
    newAddressSummary: z.string().min(1),
    targetOrderId: z.string().uuid(),
    changedAt: z.string().datetime()
}).strict();
export const FulfillmentOrderStatusChangedSchema = z.object({
    fulfillmentOrderId: z.string().uuid(),
    orderIds: z.array(z.string().uuid()),
    buyerId: z.string().uuid(),
    dealerId: z.string().uuid(),
    previousStatus: z.enum(['ACTIVE', 'SHIPPED', 'DELIVERED', 'CONFIRMED', 'ALL_CANCELLED']),
    currentStatus: z.enum(['ACTIVE', 'SHIPPED', 'DELIVERED', 'CONFIRMED', 'ALL_CANCELLED']),
    changedAt: z.string().datetime(),
    occurredAt: z.string().datetime()
}).strict();
export const InvitationSentSchema = z.object({
    invitationId: z.string().uuid(),
    inviter: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    inviteePhone: z.string().regex(/^\\+[1-9]\\d{1,14}$/),
    invitationCode: z.string().regex(/^[A-Z0-9]{9,12}$/),
    sentAt: z.string().datetime(),
    expiresAt: z.string().datetime()
}).strict();
export const InvitationAcceptedSchema = z.object({
    invitationId: z.string().uuid(),
    inviter: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    invitee: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    invitationCode: z.string().regex(/^[A-Z0-9]{9,12}$/),
    acceptedAt: z.string().datetime()
}).strict();
export const InvitationExpiredSchema = z.object({
    invitationId: z.string().uuid(),
    inviter: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    invitationCode: z.string().regex(/^[A-Z0-9]{9,12}$/),
    expiredAt: z.string().datetime()
}).strict();
export const UserBlockedSchema = z.object({
    relationshipId: z.string().uuid(),
    blockerId: z.string().uuid(),
    blockedId: z.string().uuid(),
    blockedAt: z.string().datetime()
}).strict();
export const UserUnblockedSchema = z.object({
    relationshipId: z.string().uuid(),
    blockerId: z.string().uuid(),
    blockedId: z.string().uuid(),
    unblockedAt: z.string().datetime()
}).strict();
export const UserFollowedSchema = z.object({
    relationshipId: z.string().uuid(),
    follower: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    followee: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    followedAt: z.string().datetime()
}).strict();
export const UserUnfollowedSchema = z.object({
    relationshipId: z.string().uuid(),
    follower: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    followee: z.object({
        userId: z.string(),
        nickname: z.string(),
        profileImageUrl: z.string().nullable()
    }).strict(),
    unfollowedAt: z.string().datetime()
}).strict();
export const UserReportedSchema = z.object({
    reportId: z.string().uuid(),
    reporterId: z.string().uuid(),
    reportedId: z.string().uuid(),
    categoryId: z.string().uuid(),
    targetType: z.enum(['USER', 'BROADCAST', 'CHAT', 'POST', 'PRODUCT']),
    targetEntityId: z.string().uuid().nullable().optional(),
    chatType: z.enum(['BROADCAST_CHAT', 'PRIVATE_DM', 'OPEN_CHAT']).nullable().optional(),
    messageId: z.string().uuid().nullable().optional(),
    reason: z.string().optional(),
    detailedMessage: z.string().max(2000).nullable().optional(),
    reportedAt: z.string().datetime()
}).strict();
export const ReportReviewedSchema = z.object({
    reportId: z.string().uuid(),
    reporterId: z.string().uuid(),
    reportedId: z.string().uuid(),
    reviewedBy: z.string().uuid(),
    reviewStatus: z.enum(['RESOLVED', 'REJECTED']),
    resolution: z.string(),
    reviewedAt: z.string().datetime()
}).strict();
export const ReportReasonCategoryCreatedSchema = z.object({
    categoryId: z.string().uuid(),
    name: z.string().min(1).max(100),
    description: z.string().max(500).nullable().optional(),
    createdAt: z.string().datetime()
}).strict();
export const ReportReasonCategoryUpdatedSchema = z.object({
    categoryId: z.string().uuid(),
    name: z.string().min(1).max(100),
    description: z.string().max(500).nullable().optional(),
    updatedAt: z.string().datetime()
}).strict();
export const ReportReasonCategoryActivatedSchema = z.object({
    categoryId: z.string().uuid(),
    activatedAt: z.string().datetime()
}).strict();
export const ReportReasonCategoryDeactivatedSchema = z.object({
    categoryId: z.string().uuid(),
    deactivatedAt: z.string().datetime()
}).strict();
export const ReportReasonCategoryDeletedSchema = z.object({
    categoryId: z.string().uuid(),
    deletedAt: z.string().datetime()
}).strict();
export const CategoryCreatedSchema = z.object({
    categoryId: z.string().uuid(),
    name: z.string(),
    description: z.string().optional(),
    imageUrl: z.string().nullable().optional(),
    thumbnailImageUrl: z.string().nullable().optional(),
    parentId: z.string().uuid().nullable().optional(),
    displayOrder: z.number().int(),
    createdAt: z.string().datetime()
}).strict();
export const CategoryUpdatedSchema = z.object({
    categoryId: z.string().uuid(),
    name: z.string(),
    description: z.string().optional(),
    imageUrl: z.string().nullable().optional(),
    thumbnailImageUrl: z.string().nullable().optional(),
    parentId: z.string().uuid().nullable().optional(),
    displayOrder: z.number().int(),
    updatedAt: z.string().datetime(),
    previousName: z.string().optional()
}).strict();
export const CategoryDeletedSchema = z.object({
    categoryId: z.string().uuid(),
    deletedAt: z.string().datetime()
}).strict();
//# sourceMappingURL=schemas.js.map